import { ShieldCheck, Check, X } from 'lucide-react';
import { PageHeader, ScaffoldBanner, Section } from '../components/Scaffold';

/**
 * Roles & permission matrix.
 *
 * The permission model is defined in backend internal_auth.py
 * (SUPERADMIN_PERMISSIONS / ADMIN_PERMISSIONS) and surfaced on the token via
 * GET /api/internal/auth/me. This page visualises that matrix. Editing /
 * assigning roles per-user would need a new backend endpoint (planned).
 */
const PERMISSIONS = [
  'view_cafe_registry', 'edit_cafe_details', 'view_subscriptions', 'modify_pricing',
  'view_financial_analytics', 'export_reports', 'trigger_invoices', 'suspend_reactivate_cafes',
  'view_pc_health', 'remote_pc_access', 'execute_pc_commands', 'view_audit_logs',
  'manage_users', 'manage_permissions',
];
const ADMIN = new Set([
  'view_cafe_registry', 'view_subscriptions', 'view_financial_analytics', 'view_pc_health', 'view_audit_logs',
]);

export default function RolesPermissions() {
  return (
    <div className="page roles-permissions-page">
      <style>{`
        .roles-permissions-page {
          /* noLag rebrand: scoped to this page only. No Figma node exists
             for Roles & Permissions — applied by the same accent-only
             pattern as the other scaffold pages this session. */
          --accent-primary: #FE7B00;
          --accent-primary-rgb: 254, 123, 0;
          --accent-primary-glow: rgba(254, 123, 0, 0.25);
          --accent-primary-subtle: rgba(254, 123, 0, 0.12);
          --accent-gradient: linear-gradient(135deg, #FE7B00 0%, #F7302F 100%);
        }
      `}</style>
      <PageHeader title="Roles &amp; Permissions" subtitle="Platform role model and capability matrix" />

      <ScaffoldBanner
        status="ready"
        endpoints={['GET /api/internal/auth/me (returns role + permissions)']}
        note="The matrix below mirrors SUPERADMIN_PERMISSIONS / ADMIN_PERMISSIONS in internal_auth.py. Per-user role editing needs a new backend endpoint."
      />

      <Section title="Capability matrix" desc="Super Admin vs platform Admin">
        <div className="card table-card">
          <table className="data-table">
            <thead>
              <tr>
                <th>Permission</th>
                <th style={{ textAlign: 'center' }}>Super Admin</th>
                <th style={{ textAlign: 'center' }}>Admin</th>
              </tr>
            </thead>
            <tbody>
              {PERMISSIONS.map((p) => (
                <tr key={p}>
                  <td className="font-mono">{p}</td>
                  <td style={{ textAlign: 'center' }}>
                    <Check size={16} color="var(--status-success)" />
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {ADMIN.has(p)
                      ? <Check size={16} color="var(--status-success)" />
                      : <X size={16} color="var(--text-quaternary)" />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section title="Roles">
        <div className="stat-grid">
          <div className="card">
            <div className="section__title"><ShieldCheck size={16} /> Super Admin</div>
            <div className="section__desc">Full platform control · bypasses tenant RLS (audited)</div>
          </div>
          <div className="card">
            <div className="section__title"><ShieldCheck size={16} /> Admin</div>
            <div className="section__desc">Read-only platform visibility</div>
          </div>
        </div>
      </Section>
    </div>
  );
}
